import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";
import { CheckCircle2, AlertTriangle, XCircle, Wrench } from "lucide-react";

const TONE = {
    operational:    { cls: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: CheckCircle2, label: "Operational" },
    degraded:       { cls: "text-amber-800 bg-amber-50 border-amber-200",       icon: AlertTriangle, label: "Degraded" },
    partial_outage: { cls: "text-orange-800 bg-orange-50 border-orange-200",    icon: AlertTriangle, label: "Partial outage" },
    major_outage:   { cls: "text-rose-800 bg-rose-50 border-rose-200",          icon: XCircle,       label: "Major outage" },
    maintenance:    { cls: "text-blue-800 bg-blue-50 border-blue-200",          icon: Wrench,        label: "Maintenance" },
};

/** Public /status — real component statuses only, no fabricated uptime. */
export default function Status() {
    const [d, setD] = useState(null);

    useEffect(() => {
        const url = process.env.REACT_APP_BACKEND_URL + "/api/status/public";
        const load = () => axios.get(url).then((r) => setD(r.data)).catch(() => setD({ components: [], incidents: [] }));
        load();
        const iv = setInterval(load, 30000);
        return () => clearInterval(iv);
    }, []);

    const allOk = d?.components?.every((c) => c.status === "operational");
    const headlineTone = !d ? "text-stone-500" : allOk ? "text-emerald-700" : "text-amber-800";

    return (
        <div className="min-h-screen bg-stone-50 text-stone-900">
            <div className="max-w-4xl mx-auto px-6 pt-10 pb-24">
                <div className="flex items-center justify-between mb-10">
                    <Link to="/"><BrandLogo height={32} /></Link>
                    <Link to="/trust" className="text-[12.5px] text-stone-600 hover:text-stone-900" data-testid="status-to-trust">
                        Trust Centre →
                    </Link>
                </div>

                <div className="mb-10">
                    <div className="eyebrow text-amber-700">System Status</div>
                    <h1 className={`font-display text-5xl sm:text-6xl leading-none mt-3 ${headlineTone}`}>
                        {!d ? "Checking…" : allOk ? "All systems operational." : "Some services impacted."}
                    </h1>
                    <p className="text-sm text-stone-500 mt-5">
                        Live from our platform. Refreshes every 30 seconds.
                        {d?.as_of && <> As of {new Date(d.as_of).toLocaleTimeString()}.</>}
                    </p>
                </div>

                <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="status-components">
                    {d?.components?.map((c) => {
                        const t = TONE[c.status] || TONE.operational;
                        const Icon = t.icon;
                        return (
                            <div key={c.key}
                                 data-testid={`status-${c.key}`}
                                 className="px-5 py-4 border-b border-stone-100 last:border-b-0 flex items-center gap-3">
                                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-widest ${t.cls}`}>
                                    <Icon size={11} /> {t.label}
                                </span>
                                <span className="text-[14px] font-medium text-stone-900">{c.label}</span>
                            </div>
                        );
                    })}
                </div>

                {d?.incidents?.length > 0 && (
                    <div className="mt-10">
                        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-3">Recent incidents</h2>
                        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="status-incidents">
                            {d.incidents.map((i, idx) => (
                                <div key={idx} className="py-3 border-b border-stone-100 last:border-b-0">
                                    <div className="text-[14px] font-semibold text-stone-900">{i.title}</div>
                                    <div className="text-[12px] text-stone-500 mt-1">
                                        {i.started_at ? new Date(i.started_at).toLocaleString() : ""}
                                        {i.resolved_at ? ` · resolved ${new Date(i.resolved_at).toLocaleString()}` : " · ongoing"}
                                    </div>
                                    {i.summary && <p className="text-[13px] text-stone-700 mt-2">{i.summary}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <p className="mt-10 text-[12px] text-stone-500 text-center max-w-lg mx-auto">
                    {d?.note}
                </p>
            </div>
        </div>
    );
}
